import React, { useEffect, useRef } from "react";
import { Box, Grid, Paper, TextField, Typography } from "@mui/material";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useLocale, useMessage } from "../i18n/locale";
import {
  SOUTH_AFRICA_MAP_VIEW,
  SOUTH_AFRICA_ROAD_STYLE,
} from "./discoveryMap.utils";
import { validCoordinates } from "./locationFineTuner.utils";

const MAP_STYLE =
  process.env.REACT_APP_MAP_STYLE_URL || SOUTH_AFRICA_ROAD_STYLE;

export default function LocationFineTuner({ value, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);
  const { direction } = useLocale();
  const title = useMessage("editPageLocationTitle");
  const hint = useMessage("editPageLocationHint");
  const mapLabel = useMessage("editPageLocationMapLabel");
  const latitudeLabel = useMessage("editPageLatitude");
  const longitudeLabel = useMessage("editPageLongitude");

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initialValue = initialValueRef.current;
    const hasPosition = validCoordinates(initialValue);
    const center = hasPosition ? [Number(initialValue.lng), Number(initialValue.lat)] : SOUTH_AFRICA_MAP_VIEW.center;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center,
      zoom: hasPosition ? 15 : SOUTH_AFRICA_MAP_VIEW.zoom,
      attributionControl: false,
    });
    const controlSide = direction === "rtl" ? "left" : "right";
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), `top-${controlSide}`);
    map.addControl(new maplibregl.AttributionControl({ compact: true }), `bottom-${controlSide}`);
    map.on("click", ({ lngLat }) => {
      onChangeRef.current({ lat: lngLat.lat, lng: lngLat.lng });
    });
    mapRef.current = map;
    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [direction]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !validCoordinates(value)) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }
    const position = [Number(value.lng), Number(value.lat)];
    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ draggable: true })
        .setLngLat(position)
        .addTo(map);
      markerRef.current.on("dragend", () => {
        const { lat, lng } = markerRef.current.getLngLat();
        onChangeRef.current({ lat, lng });
      });
      map.easeTo({ center: position, zoom: Math.max(map.getZoom(), 15) });
    } else {
      markerRef.current.setLngLat(position);
    }
  }, [value]);

  const updateCoordinate = (key) => (event) => {
    onChange({
      ...(value || {}),
      [key]: event.target.value,
    });
  };

  return (
    <Box>
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
        {hint}
      </Typography>
      <Paper variant="outlined" sx={{ height: 300, overflow: "hidden", borderRadius: 3 }}>
        <Box
          ref={containerRef}
          role="region"
          aria-label={mapLabel}
          dir={direction}
          sx={{ height: "100%" }}
        />
      </Paper>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            type="number"
            label={latitudeLabel}
            value={value?.lat ?? ""}
            onChange={updateCoordinate("lat")}
            slotProps={{ htmlInput: { min: -90, max: 90, step: "any" } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            type="number"
            label={longitudeLabel}
            value={value?.lng ?? ""}
            onChange={updateCoordinate("lng")}
            slotProps={{ htmlInput: { min: -180, max: 180, step: "any" } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
